import { Account, AccountHistoryEventEnum } from './account';
import { ClientQueryStatusEnum } from './client-query';
import { CWTBehaviourEnum, WorksheetSchema } from './custom-worksheet-v2';
import { InternalCommentStatusEnum } from './internal-comment';
import { JournalStatusEnum } from './journal';
import { NatureEnum } from './nature';
import { UserOverview } from './user';
import { WorkpaperRoleEnum } from './workpaper';
import { WorksheetChecklistItemOverview } from './worksheet-checklist-item';
import { WorksheetEntryStatusEnum } from './worksheet-entry';

export type WorksheetStatistics = {
    unprepared: number;
    inProgress: number;
    awaitingReview: number;
    reviewed: number;
    excluded: number;
};

export enum WorksheetTypeEnum {
    RECONCILIATION = 'RECONCILIATION',
    GENERIC = 'GENERIC',
}

export enum WorksheetStatusEnum {
    UNPREPARED = 'UNPREPARED',
    IN_PROGRESS = 'IN_PROGRESS',
    AWAITING_REVIEW = 'AWAITING_REVIEW',
    REVIEWED = 'REVIEWED',
    EXCLUDED = 'EXCLUDED',
}

export enum WorksheetEntryTypeEnum {
    GENERIC_ENTRY = 'GENERIC_ENTRY',
    RECONCILIATION_ENTRY = 'RECONCILIATION_ENTRY',
}

type WorksheetEntry = {
    id: string;
    type: WorksheetEntryTypeEnum;
    worksheetId: string;
    workpaperId: string;
    reference: number;
    sequence: number;
    description?: string | null;
    date: Date;
    createdAt: Date;
    updatedAt: Date;
    attachments: string[];
    status: WorksheetEntryStatusEnum;
    priorEntryId?: string | null;
};

export type WorksheetJournalOverview = { id: string; status: JournalStatusEnum };
export type WorksheetFileOverview = { id: string; entries: { id: string; reference: number }[] };
export type WorksheetClientQueryOverview = { id: string; status: ClientQueryStatusEnum };
export type WorksheetInternalCommentOverview = { id: string; status: InternalCommentStatusEnum };

export type EntrySource = {
    type: 'CALCULATION';
    id: string;
    metadata: {
        fieldId: string;
    };
} | null;

export type ReconciliationWorksheetEntry = WorksheetEntry & {
    type: WorksheetEntryTypeEnum.RECONCILIATION_ENTRY;
    amount: number;
    nature: NatureEnum;
};

export type GenericWorksheetEntry = WorksheetEntry & {
    type: WorksheetEntryTypeEnum.GENERIC_ENTRY;
};

export type Calculation = {
    id: string;
    name: string;
    tags: string[];
    description: string | null;
    templateId: string;
    schema: WorksheetSchema;
    firmId: string;
    workpaperId: string;
    createdAt: Date;
    updatedAt: Date;
    allowAsSchedule: boolean;
    behaviour: CWTBehaviourEnum;
    worksheets: { id: string; name: string }[];
    priorCalculationId?: string | null;
};

export type WorksheetBase = {
    id: string;
    workpaperId: string;
    status: WorksheetStatusEnum;
    createdAt: string;
    updatedAt: string;
    assigned: (UserOverview & { role: WorkpaperRoleEnum | null })[];
    checklist: WorksheetChecklistItemOverview[];
    files: WorksheetFileOverview[];
    clientQueries: WorksheetClientQueryOverview[];
    internalComments: WorksheetInternalCommentOverview[];
    name: string;
    tag: string | null;
    description: string | null;
    calculations: Calculation[];
    priorWorksheetId?: string | null;
    priorClosingAmount: {
        debit: number | null;
        credit: number | null;
    } | null;
};

export type ReconciliationWorksheetEntryOverview = Pick<ReconciliationWorksheetEntry, 'id' | 'amount' | 'nature'>;
export type GenericWorksheetEntryOverview = {
    id: string;
    amount: null;
    nature: null;
};

export type ReconciliationWorksheet = WorksheetBase & {
    account: Account;
    unreconciledCr?: number | null;
    unreconciledDr?: number | null;
    entries: ReconciliationWorksheetEntryOverview[];
    journals: WorksheetJournalOverview[];
    type: WorksheetTypeEnum.RECONCILIATION;
    createdBy: null;
};

export type GenericWorksheet = WorksheetBase & {
    account?: Account | null;
    type: WorksheetTypeEnum.GENERIC;
    entries: GenericWorksheetEntryOverview[];
    journals?: WorksheetJournalOverview[];
    createdBy: UserOverview;
    schema: null;
    templateId: null;
};

export type Worksheet = ReconciliationWorksheet | GenericWorksheet;

export type WorksheetWithAccount =
    | ReconciliationWorksheet
    | (Omit<GenericWorksheet, 'account' | 'journals'> & {
          account: Account;
          journals: WorksheetJournalOverview[];
      });

export type WorksheetEntryV2 = {
    id: string;
    worksheetId: string;
    workpaperId: string;
    sequence: number;
    date: string;
    reference: number;
    description?: string;
    attachments: string[];
    type: WorksheetEntryTypeEnum;
    amount: number | null;
    nature: NatureEnum | null;
    createdAt: Date;
    updatedAt: Date;
    priorEntryId?: string | null;
    source?: EntrySource | null;
};

export enum WorksheetHistoryEventEnum {
    WORKSHEET_CREATE = 'WORKSHEET_CREATE',
    WORKSHEET_RESET = 'WORKSHEET_RESET',
    WORKSHEET_FIELD_CHANGE = 'WORKSHEET_FIELD_CHANGE',
    WORKSHEET_TYPE_CHANGE = 'WORKSHEET_TYPE_CHANGE',
    ENTRY_CREATE = 'ENTRY_CREATE',
    ENTRY_UPDATE = 'ENTRY_UPDATE',
    ENTRY_DELETE = 'ENTRY_DELETE',
    ENTRY_ATTACHMENT_UPDATE = 'ENTRY_ATTACHMENT_UPDATE',
    CHECKLIST_ITEM_CHECK = 'CHECKLIST_ITEM_CHECK',
    CHECKLIST_ITEM_UNCHECK = 'CHECKLIST_ITEM_UNCHECK',
    CHECKLIST_ITEM_NOT_APPLICABLE = 'CHECKLIST_ITEM_NOT_APPLICABLE',
    STATUS_CHANGE = 'STATUS_CHANGE',
    ASSIGNEE_ADD = 'ASSIGNEE_ADD',
    ASSIGNEE_REMOVE = 'ASSIGNEE_REMOVE',
    WORKSHEET_DETAILS_UPDATE = 'WORKSHEET_DETAILS_UPDATE',
    CALCULATION_ADDED = 'CALCULATION_ADDED',
    CALCULATION_REMOVED = 'CALCULATION_REMOVED',
    WORKSHEET_ARCHIVED = 'WORKSHEET_ARCHIVED',
    WORKSHEET_UNARCHIVED = 'WORKSHEET_UNARCHIVED',
    WORKSHEET_LINK_PRIOR = 'WORKSHEET_LINK_PRIOR',
}

export type WorksheetHistoryRecord = {
    type: WorksheetHistoryEventEnum;
    id: string;
    worksheetId: string;
    createdAt: Date;
    createdBy: string;
    message: string;
};

export type WorksheetHistoryResult = {
    id: string;
    worksheetId?: string;
    accountId?: string;
    eventType: WorksheetHistoryEventEnum | AccountHistoryEventEnum;
    message: string;
    createdAt: Date;
    createdBy?: UserOverview;
};

export type WorksheetReviewCondition = { description: string; satisfied: boolean };

export enum HandleLinkedResourceAction {
    DELETE = 'DELETE',
}
export enum HandleLinkedResourceType {
    ENTRY = 'ENTRY',
}
